import { ReactNode } from 'react'
import { Drawer, Stack, styled } from '@mui/material'
import { tokens } from 'ComponentLibrary/style-dictionary/output'
import { XIcon } from '@phosphor-icons/react'
import { IconButton } from 'ComponentLibrary/src/piazza'
import ErrorBoundary from 'ComponentLibrary/src/templates/ErrorBoundary/ErrorBoundary'
import { TASK_ATTACHMENT_HEADER_TOTAL_HEIGHT } from 'ComponentLibrary/src/widgets/TaskDetailsDrawer/TaskAttachments/TaskAttachments'

type TaskDetailsDrawerProps = {
    isOpen: boolean
    showCloseDrawerButton?: boolean
    handleCloseDrawer: () => void
    header: ReactNode
    orderDetailsComponent: ReactNode
    taskChangeLogComponent: ReactNode
    taskDetailsComponent: ReactNode
    taskDocumentsComponent: ReactNode
    taskThreadComponent: ReactNode
}

export const TaskDetailsDrawer = ({
    isOpen,
    showCloseDrawerButton = true,
    handleCloseDrawer,
    header,
    orderDetailsComponent,
    taskChangeLogComponent,
    taskDetailsComponent,
    taskDocumentsComponent,
    taskThreadComponent,
}: TaskDetailsDrawerProps) => {
    return (
        <Drawer open={isOpen} anchor="right" onClose={handleCloseDrawer} PaperProps={{ sx: { width: '1208px', height: '100vh' } }}>
            <Stack
                direction="row"
                padding={tokens.SizingSpacing300}
                justifyContent="space-between"
                borderBottom={`1px solid ${tokens.ColorBorderDefault}`}
                height={HEADER_HEIGHT}
                boxSizing="border-box"
                alignItems="center"
            >
                {header}
                {showCloseDrawerButton && (
                    <IconButton
                        ariaLabel="close-drawer"
                        variant="tertiary"
                        size="medium"
                        icon={<XIcon color={tokens.ColorIconBoldest} />}
                        onClick={handleCloseDrawer}
                    />
                )}
            </Stack>
            <Stack direction="row" height={`calc(100% - ${HEADER_HEIGHT})`}>
                <DrawerColumnContainer flex={'1 0 25%'} maxWidth="25%">
                    <ErrorBoundary>
                        <DrawerRowInColumnContainer flex={5} overflow="hidden">
                            {orderDetailsComponent}
                        </DrawerRowInColumnContainer>
                    </ErrorBoundary>
                    <ErrorBoundary>
                        <DrawerRowInColumnContainer flex={1} overflow="hidden" alignItems="end">
                            {taskChangeLogComponent}
                        </DrawerRowInColumnContainer>
                    </ErrorBoundary>
                </DrawerColumnContainer>
                <DrawerColumnContainer flex={'2 0 37.5%'} maxWidth="37.5%">
                    <ErrorBoundary>
                        <DrawerRowInColumnContainer maxHeight={`calc(100% - ${TASK_ATTACHMENT_HEADER_TOTAL_HEIGHT})`} bottomBorder>
                            {taskDetailsComponent}
                        </DrawerRowInColumnContainer>
                    </ErrorBoundary>
                    <ErrorBoundary>
                        <DrawerRowInColumnContainer overflow="hidden">{taskDocumentsComponent}</DrawerRowInColumnContainer>
                    </ErrorBoundary>
                </DrawerColumnContainer>
                <DrawerColumnContainer flex={'2 1 37.5%'} maxWidth="37.5%">
                    <ErrorBoundary>
                        <DrawerRowInColumnContainer overflow="hidden">{taskThreadComponent}</DrawerRowInColumnContainer>
                    </ErrorBoundary>
                </DrawerColumnContainer>
            </Stack>
        </Drawer>
    )
}

const HEADER_HEIGHT = '84px'

const DrawerColumnContainer = styled('div')<{ flex: string; maxWidth?: string }>(({ flex, maxWidth }) => ({
    display: 'flex',
    flexDirection: 'column',
    maxWidth,
    flex,
    alignItems: 'center',
    '&:not(:last-child)': {
        borderRight: `1px solid ${tokens.ColorBorderDefault}`,
    },
}))

const DrawerRowInColumnContainer = styled('div')<{
    flex?: number
    bottomBorder?: boolean
    alignItems?: string
    maxHeight?: string
    overflow?: string
}>(({ flex = 1, bottomBorder, alignItems = 'center', overflow, maxHeight }) => ({
    class: 'row',
    display: 'flex',
    overflow,
    maxHeight,
    flex,
    alignItems,
    width: '100%',
    ...(bottomBorder && {
        '&:not(:last-child)': {
            borderBottom: `1px solid ${tokens.ColorBorderDefault}`,
        },
    }),
}))
